import React, { useState, useEffect, useMemo } from "react";
import { withRouter } from "react-router-dom";
import { renderRoutes, matchRoutes } from "react-router-config";
import { connect } from "react-redux";
import { parse } from "query-string";
import { mapProperties, hydrateRoute } from "colbi_web_ui/lib/utils";
import {
    applyThemeWithPriority,
    GLOBAL_HEADER_HEIGHT,
} from "../../utils/utils";
import { buildImportIdOptionFromTableLabel } from "../../utils/importIdUtils";
import HeaderNewNavigation from "./HeaderNewNavigation";
import Connect from "colbi_web_ui/lib/components/Connect";
import {
    asideRepositoryNavigationByModule,
    filteredNavigationByModulesForNestedLinks,
} from "../Repository/navigation";

import styles from "./RawData.module.sass";

import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    downloadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    removeModal,
    setDataFileStatusAction,
    deleteFilePermanentlyAction,
    deleteInvalidFilePermanentlyAction,
    hideFileAction,
    downloadFileAction,
    reprocessRepositoryAction,
    genericSearchAction,
    getFindingDetails,
    reprocessFileAction,
    getRawTablesAvailable,
    getRawDataModulesWithViewMode,
    updateRowsAction,
    downloadCsvStreamAction
} from "../../store/actions";
import I18n from "../../I18n";
import useUpdateThemes from "../../hooks/useUpdateThemes";

import proxy from "../../routes/proxy";
import {
    filteredQueriesByModules,
    filteredQueriesByModulesForMenu,
    getFirstModuleOfArray,
} from "../../routes/manageRouting";

import RepositoryStatus from "../../components/RepositoryStatus/RepositoryStatus";

import { mapPropertiesForMenu } from "../../utils";
import {
    decodeUserSession,
    redirectNotValid,
    buildStaticLinks,
} from "../../utils/utils";
import { cleanPropertiesNestedInMenu } from "../../utils/mapPropertiesForMenu";
import { buildNavProps, NewNavigationMenu } from "../utils/utils";
import { urlRedirectLink } from "../../utils/routingUtils";
import GlobalLayout from "../../components/GlobalLayout/GlobalLayout";
import {
    SovosBox,
    SovosCircularProgress,
    SovosSnackbar,
    SovosPageLayout,
    SovosPageContentGeneric,
    SovosTypography,
} from "mosaic-react";
import { useAuth } from "react-oidc-context";
import HeaderEditMode from "../RawData/Header/HeaderEditMode";
import {
    mapRawDataByLine,
    onCellHighlightDataGrid,
    onRowUpdateError,
    updateRawDataHeaderArray,
} from "../RawData/utils";

const RawData = (props) => {
    const {
        modules,
        user,
        fetch,
        match,
        route,
        location,
        history,
        reload,
        getRawTablesAvailable,
        getRawDataModulesWithViewMode,
        updateRowsAction
    } = props;
    const { params: matchParams } = match || {};
    let { fiscalYear, importId } = matchParams;
    const { projectId, modulePrefix, locale = "en", entityRawData } = matchParams;
    const { results: fetchResults, status: fetchStatus } = fetch || {};
    const [editMode, setEditMode] = useState(false);
    const [topOffset, setTopOffset] = useState(GLOBAL_HEADER_HEIGHT);

    let rowsEdited = {};
    let listResults = [];
    let rawDataEdit = false;

    const auth = useAuth();

    const userInformation = decodeUserSession(user);

    const i18n = I18n.use(locale);

    const defaultNotification = { open: false, message: "", type: "", onClose: () => { } };
    const [notification, pushNotification] = useState({ ...defaultNotification });

    // --- viewMode state (read from URL query param) ---
    const queryParams = parse(location.search) || {};
    const viewMode = queryParams.viewMode || "source";

    const handleViewModeChange = (newViewMode) => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.set("viewMode", newViewMode);

        // Navigate to the base raw_data landing page (without module/entity) so that
        // stale table data from the previous viewMode is not shown.
        const localePart = locale ? `/${locale}` : "";
        const basePath = `${localePart}/${projectId}/${fiscalYear || "0000"}/${importId || "full"}/raw_data`;
        history.replace({
            pathname: basePath,
            search: `?${searchParams.toString()}`,
        });
    };

    const { menuOrganizationProject = [] } = mapProperties(
        {
            menuOrganizationProject: "query(0).list",
        },
        fetchResults,
        fetchStatus,
        RawData.queries(matchParams),
        props,
        matchParams,
        i18n,
        locale,
    );

    const { organizationProjects: orgProjectsFromMap = {}, status } = mapProperties(
        {
            organizationProjects: "query(1)",
            status: "status(0)",
        },
        fetchResults,
        fetchStatus,
        RawData.queries(matchParams),
        props,
        matchParams,
        i18n,
        locale,
    );

    // Fallback: read organization projects directly from fetchResults using the known cache key.
    // The DataLoader stores data under: ${locale}_organization_projects_by_projectId_projectId_${projectId}
    const orgDirectKey = `${locale}_organization_projects_by_projectId_projectId_${projectId}`;
    const orgProjectsDirect = fetchResults?.[orgDirectKey];
    const organizationProjects = (orgProjectsDirect?.projects?.length > 0)
        ? orgProjectsDirect
        : orgProjectsFromMap;

    const { loading = true, success = false } = status || {};

    const { updateThemes, LoadingComponent } = useUpdateThemes(loading, success);

    // Apply theme with priority logic: Project > Organization > Default
    useEffect(() => {
        applyThemeWithPriority(organizationProjects, projectId, updateThemes, "RawData");
    }, [organizationProjects, projectId, updateThemes]);

    const { rawDataModules = [] } = mapProperties(
        {
            rawDataModules: "query(3).list",
        },
        fetchResults,
        fetchStatus,
        RawData.queries(matchParams),
        props,
        matchParams,
        i18n,
        locale,
    );

    // --- Fetch module viewMode info independently (avoids polluting the main batch query) ---
    const [rawDataModulesInfo, setRawDataModulesInfo] = useState([]);
    const [modulesInfoLoaded, setModulesInfoLoaded] = useState(false);

    useEffect(() => {
        if (!projectId || !fiscalYear) return;

        setModulesInfoLoaded(false);
        getRawDataModulesWithViewMode(projectId, fiscalYear)
            .then((result) => {
                setRawDataModulesInfo(Array.isArray(result) ? result : []);
                setModulesInfoLoaded(true);
            })
            .catch(() => {
                setRawDataModulesInfo([]);
                setModulesInfoLoaded(true);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, fiscalYear]);

    // Only hide the Legal tab once data has loaded and confirmed no legal modules exist.
    // While loading, keep both tabs visible to avoid a jarring layout shift.
    const hasLegalModules = rawDataModulesInfo.some((m) => m?.viewMode === "legal");
    const disableLegalTab = modulesInfoLoaded && !hasLegalModules;

    // Build a map of module name → viewMode for quick lookup
    const moduleViewModeMap = rawDataModulesInfo.reduce((acc, m) => {
        if (m?.module) acc[m.module] = m.viewMode || "source";
        return acc;
    }, {});

    // --- Fetch tables_available for each module (same logic as old Header) ---
    const [moduleTablesMap, setModuleTablesMap] = useState({});

    useEffect(() => {
        if (!rawDataModules || rawDataModules.length === 0 || !projectId || !fiscalYear) return;

        Promise.all(
            rawDataModules.map((mod) =>
                getRawTablesAvailable(projectId, mod, fiscalYear)
                    .then((res) => ({ mod, tables: res?.tables_available || [] }))
                    .catch(() => ({ mod, tables: [] }))
            )
        ).then((results) => {
            const newMap = {};
            results.forEach(({ mod, tables }) => {
                newMap[mod] = tables;
            });
            setModuleTablesMap(newMap);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawDataModules, projectId, fiscalYear]);

    // --- Build tree entries for MainMenuContent from rawDataModules + tables_available ---
    const rawDataTreeEntries = useMemo(() => {
        // Wait until viewMode info has loaded so we don't flash all modules as "source"
        // before knowing which belong to source vs legal.
        if (!modulesInfoLoaded) return [];

        // Only include modules whose viewMode matches the currently selected viewMode.
        // Modules without an explicit viewMode default to "source".
        const filteredModules = rawDataModules.filter(
            (mod) => (moduleViewModeMap[mod] || "source") === viewMode,
        );

        return filteredModules.map((mod) => {
            const tables = moduleTablesMap[mod] || [];
            return {
                id: `RawData_${mod}`,
                label: mod,
                useInMenuTreeList: true,
                viewMode: viewMode,
                nestedLinks: [...new Set(
                    tables.map((table) => table.split(" - ")[1]?.trim() || table)
                )]
                    .filter(Boolean)
                    .map((entityName) => ({
                        id: `RawData_${mod}_${entityName}`,
                        content: entityName,
                        label: entityName,
                        key: "data",
                        to: `/${locale}/${projectId}/${fiscalYear}/${importId || "full"}/${mod}/raw_data/${entityName}${viewMode ? `?viewMode=${viewMode}` : ""}`,
                    })),
            };
        });
    }, [modulesInfoLoaded, rawDataModules, moduleViewModeMap, moduleTablesMap, viewMode, locale, projectId, fiscalYear, importId]);

    const getTotalExportLines = fetchResults[`${locale}_get_limit_export`] || "";

    const project = organizationProjects
        ? (organizationProjects.projects || []).find(({ id }) => String(id) === String(projectId)) || {
            name: "",
        }
        : { name: "" };

    const { fiscalYears: projectFiscalYears } = project || {};

    const fiscalYears = Object.keys(projectFiscalYears || []);

    if (!fiscalYear && project && projectFiscalYears) {
        fiscalYear = fiscalYears.length > 0 ? Math.max(...fiscalYears) : "";
    }

    const fiscalYearsWithImportIds = projectFiscalYears || {};

    // fiscalYears on the project is a flat object: { "2024": [{ id, name }, ...] }
    let importIdsFromUrlFiscalYear = fiscalYearsWithImportIds[fiscalYear];

    // Fallback: if the backend returned empty fiscalYears (e.g. data_files not in imported
    // status), derive the available import IDs from moduleTablesMap — already fetched for
    // the sidebar via getRawTablesAvailable. Format is "importId - TableName".
    if (!importIdsFromUrlFiscalYear && Object.keys(moduleTablesMap).length > 0) {
        const idMap = new Map();
        Object.values(moduleTablesMap).flat().forEach((table) => {
            const importOption = buildImportIdOptionFromTableLabel(table);
            if (importOption) {
                idMap.set(importOption.id, importOption);
            }
        });
        if (idMap.size > 0) {
            importIdsFromUrlFiscalYear = [...idMap.values()];
        }
    }

    if (!importId && importIdsFromUrlFiscalYear && importIdsFromUrlFiscalYear.length) {
        const [maxImportId] = importIdsFromUrlFiscalYear;
        importId = maxImportId?.id;
    }


    // Build effective fiscal years map for the selector.
    // If the backend returned no fiscal year data, fall back to just the current URL fiscal year.
    const effectiveFiscalYearsWithImportIds =
        Object.keys(fiscalYearsWithImportIds).length > 0
            ? fiscalYearsWithImportIds
            : fiscalYear
                ? { [fiscalYear]: importIdsFromUrlFiscalYear || [] }
                : {};


    const repositoryStatus = fetchResults[
        `${locale}_repository_status_fiscalYear_${fiscalYear}_projectId_${projectId}`
    ] || { status: 1 };

    const { repoStatus = 0 } = repositoryStatus

    useEffect(() => {
        if (repoStatus) {
            const addValue = repoStatus ? 35 : 0
            setTopOffset(GLOBAL_HEADER_HEIGHT + addValue);
        }
    }, [repoStatus]);

    const queriesForMenu = filteredQueriesByModulesForMenu(modules);
    const navData = mapPropertiesForMenu(
        queriesForMenu,
        fetchResults,
        locale,
        fiscalYear,
        projectId,
        importId,
    );

    const firstModuleFromProject = getFirstModuleOfArray(modules);
    const moduleConfigs = project
        ? (project.modules || []).find(({ name }) => name === firstModuleFromProject)
        : undefined;
    const importIdToUse =
        moduleConfigs && moduleConfigs.navigation_by_import_id ? importId : "full";

    const params = {
        ...(parse(location.search) || {}),
        ...(matchParams || {}),
    };

    const onRowEditDatagrid = (updatedRowObject, originalRowObject) => {
        const {
            audit_logs: updatedAuditLogs,
            oldRow: updatedOldRow,
            uniqueId: updatedUniqueID,
            ...updatedRow
        } = updatedRowObject;
        const {
            audit_logs: originalAuditLogs,
            oldRow: originalOldRow,
            uniqueId: originalUniqueID,
            ...originalRow
        } = originalRowObject;
        for (const key in updatedRow) {
            if (updatedRow[key] !== originalRow[key]) {
                const updateObject = {
                    [updatedRow.id]: {
                        updated: {
                            [`${key}`]: updatedRow[key],
                            ...rowsEdited[updatedRow.id]?.updated,
                        },
                        original: rowsEdited[updatedRow.id]?.original
                            ? rowsEdited[updatedRow.id]?.original
                            : originalRow,
                    },
                };
                rowsEdited = { ...rowsEdited, ...updateObject };
            }
        }
        return { ...updatedRow, uniqueId: updatedUniqueID };
    };

    const routeQueries = (route.queries && route.queries(params)) || [];
    // Only create children (DataGrid) when entityRawData is present
    const children = entityRawData && route.components ? route.components.map((c, index) => {
        const Component = c.component;
        const componentParsedProps = mapProperties(
            c.props,
            fetchResults,
            fetchStatus,
            routeQueries,
            props,
            params,
            i18n,
            locale,
        );

        const header = updateRawDataHeaderArray(componentParsedProps);

        const customRawDataView =
            componentParsedProps?.items?.length && componentParsedProps?.header?.length
                ? mapRawDataByLine(
                    componentParsedProps.items,
                    componentParsedProps.header,
                    entityRawData,
                    projectId,
                    fiscalYear,
                    importId,
                    modulePrefix,
                )
                : [];
        const componentProps = {
            ...componentParsedProps,
            header: header,
            items: customRawDataView,
            modules,
            editMode: editMode,
        };
        if (componentProps && componentProps.items?.length && componentProps.cellHighlight) {
            componentProps.modifierssx = {
                "& .highlight": {
                    backgroundColor: "#E0EAF8",
                },
            };
            componentProps.onCellHighlight = (params) => onCellHighlightDataGrid(params);
        }
        if (componentProps && componentProps.items?.length && componentProps.onRowEdit) {
            componentProps.onProcessRowUpdateErrorAction = () =>
                onRowUpdateError(pushNotification, I18n);
            componentProps.onRowEdit = (updatedRow, originalRow) =>
                onRowEditDatagrid(updatedRow, originalRow);
        }

        rawDataEdit = componentParsedProps.rawDataEdit || false;
        listResults = componentProps.items || [];
        return (
            <Component
                key={index}
                modifiers={c.modifiers}
                {...componentProps}
                activity={fetch.activity}
            />
        );
    }) : [];

    const branch = matchRoutes([route, ...(route.routes || [])], location.pathname);
    //TODO: review logic
    const branchQueries = branch.reduce((allQueries, { route }) => {
        const innerQueries = route.queries ? route.queries(params) : [];
        allQueries = [...innerQueries];
        return allQueries;
    }, []);

    const downloadableQueries = branchQueries?.filter(({ downloadable }) => downloadable);

    const repositoryNavigation = filteredNavigationByModulesForNestedLinks(
        modules || [],
        asideRepositoryNavigationByModule,
    );

    const _navigation = (route.navigation || []).map((item) => {
        if (item?.name === "label(Repository)") {
            delete item.to;
            item.navigation = [...repositoryNavigation];
        }

        return item;
    });

    const { navigation } = mapProperties(
        { navigation: _navigation },
        fetchResults,
        fetchStatus,
        RawData.queries({ ...params, fiscalYear, importId }),
        {
            ...props,
            ...navData,
        },
        { ...params, fiscalYear, importId },
        i18n,
        locale,
    );

    //TODO Pass this to an util and take into account fetch.activity
    const isPageLoading = (status, activity) => {
        return (
            activity ||
            activity === null ||
            Object.keys(status).some((queryKey) => {
                return status[queryKey].loading === true;
            })
        );
    };

    const redirect = redirectNotValid(userInformation, locale, false, auth);

    if (redirect) {
        return redirect;
    }

    if (!projectId) {
        return urlRedirectLink(`${locale ? `/${locale}` : ""}/`);
    }

    if (!fiscalYear && !importId && !fiscalYears.length) {
        return urlRedirectLink(
            `${locale ? `/${locale}` : "/en"}/${projectId}/0000/full/repository/activity`,
        );
    }

    if (fiscalYear === "0000" && fiscalYears.length) {
        const to = hydrateRoute(match.path, {
            ...matchParams,
            fiscalYear: Math.max(...fiscalYears),
            importId: importIdToUse,
            firstModuleFromProject,
        });

        if (to !== match.url && !to.match(/\*$/)) {
            return urlRedirectLink(to);
        }
    }

    if (
        fiscalYear &&
        importIdToUse &&
        !isNaN(Number(matchParams.fiscalYear)) &&
        !isNaN(Number(matchParams.importId)) &&
        fiscalYear !== matchParams.fiscalYear &&
        importIdToUse !== matchParams.importId &&
        firstModuleFromProject
    ) {
        const to = hydrateRoute(match.path, {
            ...matchParams,
            fiscalYear,
            importId: importIdToUse,
            firstModuleFromProject,
        });

        if (to !== match.url && !to.match(/\*$/)) {
            return urlRedirectLink(to);
        }
    }

    // Only redirect to add modulePrefix when viewing a specific entity (not the landing page)
    // On the landing page (no entityRawData), we show modules/entities grouped by viewMode
    if (rawDataModules?.length && !matchParams.modulePrefix && entityRawData) {
        const to = hydrateRoute(match.path, {
            ...matchParams,
            fiscalYear: Math.max(...fiscalYears),
            importId: importIdToUse,
            modulePrefix: rawDataModules?.length ? rawDataModules[0] : "",
        });
        return urlRedirectLink(to + (location.search || ""));
    }

    const { navProps, staticLinks, leftMenuStaticLinks, hasNewNavigation } = buildNavProps(
        buildStaticLinks,
        navigation,
        history,
        matchParams,
        fiscalYear,
        importId,
        i18n,
        menuOrganizationProject,
        projectId,
        cleanPropertiesNestedInMenu,
        locale,
        proxy,
        params,
        userInformation,
        modules,
        viewMode,
    );

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries,
        params: { ...params, fiscalYear, importId },
        repositoryStatus,
        getTotalExportLines,
        staticLinks,
        styles,
        organizationProjects,
        leftMenuStaticLinks: hasNewNavigation ? leftMenuStaticLinks : [],
    };

    const saveRawData = (e) => {
        updateRowsAction(
            projectId,
            modulePrefix,
            fiscalYear,
            importId,
            entityRawData,
            JSON.stringify(rowsEdited),
            "update_rows_raw_data",
        )
            .then((res) => {
                if (!res?.update_rows_raw_data) {
                    onRowUpdateError(pushNotification, I18n);
                    return;
                }
                pushNotification({
                    open: true,
                    message: I18n.translate`Your edit have been saved successfully`,
                    type: "confirmation",
                    timeout: "short",
                });
                setEditMode(false);
                reload && reload();
            })
            .catch(() => {
                onRowUpdateError(pushNotification, I18n);
            });
    };

    const title = i18n`Raw Data`;
    const titleBarActions = entityRawData ? [
        <HeaderEditMode
            key="edit-mode"
            editMode={editMode}
            setEditMode={setEditMode}
            onClickSave={saveRawData}
            params={params}
            list={listResults}
            rawDataEdit={rawDataEdit}
        />,
    ] : undefined;

    return (
        LoadingComponent || (
            <GlobalLayout {...globalLayoutProps}>
                <SovosPageLayout
                    className={repoStatus ? `${styles["with-repository-banner"]}` : ""}
                    pageTitle={title || ""}
                    titleBarActions={titleBarActions}
                >
                    <RepositoryStatus modules={modules} />
                    <SovosPageContentGeneric
                        sx={{
                            padding: "0 !important",
                            marginLeft: "0",
                            marginRight: "24px",
                            height: "100%",
                            paddingTop: "49px !important"
                        }}
                    >
                        <SovosBox sx={{ position: "absolute", top: topOffset, left: 0, right: 0, overflowX: "hidden" }}>
                            <Connect to={["search", "downloading"]}>
                                {() => (
                                    <HeaderNewNavigation
                                        fiscalYears={effectiveFiscalYearsWithImportIds}
                                        importIds={importIdsFromUrlFiscalYear}
                                        viewMode={viewMode}
                                        onViewModeChange={handleViewModeChange}
                                        disableLegalTab={disableLegalTab}
                                    />
                                )}
                            </Connect>
                        </SovosBox>
                        <SovosBox
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                height: "100%",
                                width: "100%",
                            }}
                        >
                            {/* Left sidebar tree — same component as structured data */}
                            <NewNavigationMenu
                                menuEntries={rawDataTreeEntries}
                                history={history}
                                viewMode={viewMode}
                                loading={!modulesInfoLoaded || rawDataModules.length === 0}
                            />

                            {/* Right content area — loading only affects this area */}
                            <SovosBox sx={{ flex: 1, overflow: "auto" }}>
                                {isPageLoading(fetch?.status, fetch?.activity) ? (
                                    <SovosBox
                                        sx={{
                                            flex: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: "100%",
                                        }}
                                    >
                                        <SovosCircularProgress aria-label={"Loading"} />
                                    </SovosBox>
                                ) : entityRawData ? (
                                    <>
                                        {children}
                                        {renderRoutes(route.routes)}
                                    </>
                                ) : (
                                    <SovosBox
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: "100%",
                                            padding: "48px",
                                        }}
                                    >
                                        <SovosTypography variant="body1" sx={{ color: "text.secondary" }}>
                                            {i18n`Select a table from the menu to view its data.`}
                                        </SovosTypography>
                                    </SovosBox>
                                )}
                                <SovosSnackbar
                                    onClose={() => {
                                        pushNotification({
                                            ...notification,
                                            open: false,
                                        });
                                    }}
                                    message={notification && notification.message}
                                    open={notification && notification.open}
                                    variant={
                                        notification && notification.type
                                            ? notification.type
                                            : "confirmation"
                                    }
                                    timeout={
                                        notification && notification.timeout
                                            ? notification.timeout
                                            : "medium"
                                    }
                                    disableWindowBlurListener
                                />
                            </SovosBox>
                        </SovosBox>
                    </SovosPageContentGeneric>
                </SovosPageLayout>
            </GlobalLayout >
        )
    );
};

RawData.queries = (args, userModulesByProject) => {
    const queries = filteredQueriesByModules(userModulesByProject);
    const deepCopyQueries = JSON.parse(JSON.stringify(queries));
    if (deepCopyQueries) {
        //needed to change arguments to the respective value
        deepCopyQueries.forEach((query) =>
            Object.keys(query.args).forEach((k) =>
                query.args[k] ? query.args[k] : (query.args[k] = args[k] || query.args[k]),
            ),
        );
    }

    const allQueries = [
        {
            resource: "menu_organizations_projects",
            body: "id,name,typeEntity,nestedAccounts{id,name,typeEntity}",
            forceRefresh: true,
        },
        {
            resource: "organization_projects_by_projectId",
            args: {
                projectId: args.projectId,
                viewMode: args.viewMode || "",
            },
            body: "id,name,maxFiscalYear,fiscalYears,calendarYears,mosaicThemeConfig,projects{id,name,fiscalYears,status,taxRegistrationNumber,active,projectExpired,metadata,mosaicThemeConfig,modules{navigation_by_import_id,name,default_route,default_module}},organizationExpired, organizationActive, maxFiscalYear",
            forceRefresh: true,
        },
        {
            resource: "get_limit_export",
        },
        {
            resource: "file_type_modules_raw_data",
            args: {
                projectId: args.projectId,
            },
        },
        {
            resource: "raw_data_modules_with_entities",
            args: {
                projectId: args.projectId,
                fiscalYear: args.fiscalYear,
            },
            body: "module,viewMode,entities",
        },
        ...deepCopyQueries,
    ];
    if (userModulesByProject && userModulesByProject.includes("merge_collisions")) {
        allQueries.push({
            resource: "repository_status",
            args: {
                projectId: args.projectId,
                fiscalYear: args.fiscalYear,
            },
            body: "repoStatus,fiscalYear,fileType,autoMerge",
        });
    }
    return allQueries;
};

export default connect(
    ({ user, fetch, menu, uploads, auditNote, downloading }) => ({
        user,
        fetch,
        menu,
        uploads,
        auditNote,
    }),
    {
        logout,
        pushModal,
        popModal,
        removeModal,
        toggleMenu,
        setLocale,
        pushDialog,
        getFindingDetails,
        setDataFileStatus: setDataFileStatusAction,
        deleteFilePermanently: deleteFilePermanentlyAction,
        deleteInvalidFilePermanently: deleteInvalidFilePermanentlyAction,
        hideFile: hideFileAction,
        downloadFile: downloadFileAction,
        reprocessRepository: reprocessRepositoryAction,
        uploadFiles: uploadAction,
        download: downloadAction,
        submitAuditNote: auditNoteSubmit,
        genericSearchAction,
        reprocessFiles: reprocessFileAction,
        getRawTablesAvailable,
        getRawDataModulesWithViewMode,
        updateRowsAction,
        downloadCsvStreamAction: downloadCsvStreamAction
    },
)(withRouter(RawData));
